import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ApolloQueryResult} from 'apollo-client';
import React, {useCallback, useState} from 'react';

import {ProjectArtifactsQuery} from '../../generated/graphql';
import * as Urls from '../../util/urls';
import {usePaginate} from '../../util/usePaginate';
import * as S from './ArtifactSidebar.styles';
import {
  ArtifactSequenceHeader,
  ArtifactSidebarPaginate,
} from './ArtifactSidebarSequence';
import {ArtifactVersionSidebar} from './MetadataQueryResultsVersions';
import {
  ARTIFACTS_PER_TYPE,
  ArtifactSequenceSidebar,
  VERSIONS_PER_SEQUENCE,
} from './utils';

interface ArtifactSequenceProps {
  artifactTypeName: string;
  artifactSequence: ArtifactSequenceSidebar;
  projectName: string;
  entityName: string;
  artifactTab?: string;
  filePath?: string;
  refetchProjectArtifacts: () => Promise<
    ApolloQueryResult<ProjectArtifactsQuery>
  >;
  artifactCollectionName?: string;
}

// Component to display Artifact Sequences
export const ArtifactSequence: React.FC<ArtifactSequenceProps> = props => {
  const [expanded, setExpanded] = useState(true);
  const toggle = useCallback(() => setExpanded(s => !s), []);
  const {
    entityName,
    projectName,
    artifactTypeName,
    artifactTab,
    filePath,
    artifactSequence,
  } = props;
  const sequenceName = artifactSequence.name;

  const sortedVersionIndexes = Object.keys(artifactSequence.artifactMemberships)
    .map(Number)
    .sort((a, b) => b - a);

  const {
    totalPages,
    hasNextPage,
    hasPreviousPage,
    setPage,
    total,
    page,
    itemsPage,
  } = usePaginate(sortedVersionIndexes, VERSIONS_PER_SEQUENCE);

  return (
    <Tailwind>
      <ArtifactSequenceHeader
        {...props}
        name={sequenceName}
        id={artifactSequence.id}
        onClick={toggle}
        isPortfolio={false}
        expanded={expanded}
      />
      {expanded && (
        <S.ArtifactsSidebarArtifactList>
          {itemsPage.map(versionKey => {
            const membership = artifactSequence.artifactMemberships[versionKey];
            const url = Urls.artifactSubpage({
              entityName,
              projectName,
              artifactTypeName,
              artifactCollectionName: sequenceName,
              artifactCommitHash: `v${membership.versionIndex}`,
              tabName: artifactTab,
              pathString: filePath,
            });
            return (
              <ArtifactVersionSidebar
                key={versionKey}
                artifactMembership={membership}
                to={url}
                isSelected={false}
              />
            );
          })}
          {totalPages > 0 && (
            <S.ArtifactSidebarTypePaginate>
              <ArtifactSidebarPaginate
                size="mini"
                perPage={ARTIFACTS_PER_TYPE}
                page={page}
                total={total}
                pageInfo={{hasNextPage, hasPreviousPage}}
                onNextClick={() => setPage(s => s + 1)}
                onPreviousClick={() => setPage(s => s - 1)}
              />
            </S.ArtifactSidebarTypePaginate>
          )}
        </S.ArtifactsSidebarArtifactList>
      )}
    </Tailwind>
  );
};
